import React, { useState, useEffect } from 'react';
import { useLanguage } from '@/contexts/LanguageContext';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Calculator, Plus, Download, Mail, Trash2, LogIn } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { Link, useNavigate } from 'react-router-dom';
import MembershipDisclaimerForm from '@/components/MembershipDisclaimerForm';
import { trackCalculatorUse, trackQuoteFormSubmit } from '@/lib/analytics';

interface Product {
  id: string;
  name_en: string;
  name_es: string;
  unit_en: string;
  unit_es: string;
  unit_type: string;
  slug_en: string;
  slug_es: string;
  price_book: {
    list_price: number;
    sale_price: number | null;
    unit_label_en: string;
    unit_label_es: string;
  }[];
}

interface QuoteItem {
  id: string;
  product_id: string;
  product_name: string;
  quantity: number;
  unit: string;
  unit_price: number;
  total_price: number;
  unit_type: string;
}

interface DeliveryRate {
  delivery_type: string;
  mile_range_start: number;
  mile_range_end: number | null;
  rate: number;
  rate_type: string;
}

const EnhancedQuoteCalculator = () => {
  const { language, t } = useLanguage();
  const { user } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [products, setProducts] = useState<Product[]>([]);
  const [deliveryRates, setDeliveryRates] = useState<DeliveryRate[]>([]);
  const [items, setItems] = useState<QuoteItem[]>([]);
  const [miles, setMiles] = useState<number>(0);
  const [loading, setLoading] = useState(true);
  const [currentItem, setCurrentItem] = useState({
    product_id: '',
    quantity: 1,
    unit: 'pallet'
  });

  useEffect(() => {
    const fetchData = async () => {
      try {
        // Fetch products with pricing
        const { data: productsData, error: productsError } = await supabase
          .from('products')
          .select(`
            *,
            price_book (
              list_price,
              sale_price,
              unit_label_en,
              unit_label_es
            )
          `)
          .eq('is_active', true)
          .order('sort_order');

        if (productsError) throw productsError;

        // Fetch delivery rates
        const { data: ratesData, error: ratesError } = await supabase
          .from('delivery_rates')
          .select('*')
          .eq('is_active', true)
          .order('delivery_type, mile_range_start');

        if (ratesError) throw ratesError;

        setProducts(productsData || []);
        setDeliveryRates(ratesData || []);
      } catch (error) {
        console.error('Error fetching quote data:', error);
        toast({
          title: "Error",
          description: "Failed to load quote calculator data",
          variant: "destructive"
        });
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [toast]);

  const getProductName = (product: Product) => 
    language === 'en' ? product.name_en : product.name_es;

  const getProductPrice = (product: Product) => 
    product.price_book[0]?.sale_price || product.price_book[0]?.list_price || 0;

  const getUnitLabel = (product: Product) => 
    language === 'en' ? product.price_book[0]?.unit_label_en : product.price_book[0]?.unit_label_es;

  const calculateDeliveryFee = (miles: number, hasGrassItems: boolean, hasBulkItems: boolean) => {
    if (miles === 0) return 0;

    // Convert to roundtrip miles for delivery fee calculation
    const roundtripMiles = miles * 2;

    let grassFee = 0;
    let bulkFee = 0;

    if (hasGrassItems) {
      const grassRates = deliveryRates.filter(rate => rate.delivery_type === 'grass');
      grassFee = calculateFeeForType(roundtripMiles, grassRates);
    }

    if (hasBulkItems) {
      const bulkRates = deliveryRates.filter(rate => rate.delivery_type === 'bulk');
      bulkFee = calculateFeeForType(roundtripMiles, bulkRates);
    }

    // Return the higher of the two fees (most deliveries combine both types)
    return Math.max(grassFee, bulkFee);
  };

  const calculateFeeForType = (miles: number, rates: DeliveryRate[]) => {
    for (const rate of rates) {
      if (miles >= rate.mile_range_start && (rate.mile_range_end === null || miles <= rate.mile_range_end)) {
        if (rate.rate_type === 'per_mile') {
          return rate.rate * miles;
        }
        return rate.rate;
      }
    }
    return 0;
  };

  const addItem = () => {
    const selectedProduct = products.find(p => p.id === currentItem.product_id);
    if (!selectedProduct || currentItem.quantity <= 0) return;

    const unitPrice = getProductPrice(selectedProduct);
    const newItem: QuoteItem = {
      id: Date.now().toString(),
      product_id: selectedProduct.id,
      product_name: getProductName(selectedProduct),
      quantity: currentItem.quantity,
      unit: currentItem.unit,
      unit_price: unitPrice,
      total_price: unitPrice * currentItem.quantity,
      unit_type: selectedProduct.unit_type
    };

    setItems([...items, newItem]);
    setCurrentItem({ product_id: '', quantity: 1, unit: 'pallet' });
    trackCalculatorUse({ product_id: selectedProduct.id, product_name: newItem.product_name, quantity: newItem.quantity, unit_type: selectedProduct.unit_type, language });
  };

  const removeItem = (id: string) => {
    setItems(items.filter(item => item.id !== id));
  };

  const saveQuote = async () => {
    if (!user) {
      navigate(`/${language}/auth`, { 
        state: { from: { pathname: `/${language}/quote` } } 
      });
      return;
    }

    try {
      const hasGrassItems = items.some(item => item.unit_type === 'grass');
      const hasBulkItems = items.some(item => item.unit_type === 'bulk');
      const deliveryFee = calculateDeliveryFee(miles, hasGrassItems, hasBulkItems);
      const subtotal = items.reduce((sum, item) => sum + item.total_price, 0);
      const total = subtotal + deliveryFee;

      // Insert quote without PII (trigger will strip any PII fields automatically)
      const { data, error } = await supabase
        .from('quotes')
        .insert({
          user_id: user.id,
          delivery_miles: miles,
          subtotal: subtotal,
          delivery_fee: deliveryFee,
          total: total,
          language: language,
          items: items as any,
          status: 'draft'
        })
        .select()
        .single();

      if (error) throw error;

      trackQuoteFormSubmit({ quote_id: data.id, items_count: items.length, subtotal, delivery_fee: deliveryFee, total, delivery_miles: miles, language });

      toast({
        title: language === 'en' ? "Quote Saved" : "Cotización Guardada",
        description: language === 'en' 
          ? `Quote #${data.id.slice(0, 8)} saved successfully` 
          : `Cotización #${data.id.slice(0, 8)} guardada exitosamente`
      });
    } catch (error) {
      console.error('Error saving quote:', error);
      toast({
        title: "Error",
        description: "Failed to save quote",
        variant: "destructive"
      });
    }
  };

  const subtotal = items.reduce((sum, item) => sum + item.total_price, 0);
  const hasGrassItems = items.some(item => item.unit_type === 'grass');
  const hasBulkItems = items.some(item => item.unit_type === 'bulk');
  const deliveryFee = items.length > 0 ? calculateDeliveryFee(miles, hasGrassItems, hasBulkItems) : 0;
  const total = subtotal + deliveryFee;

  if (loading) {
    return (
      <section className="py-16 bg-gradient-subtle">
        <div className="container mx-auto px-4">
          <div className="text-center">
            <Calculator className="h-12 w-12 text-primary mx-auto mb-4 animate-pulse" />
            <p className="text-lg text-muted-foreground">Loading quote calculator...</p>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="py-16 bg-gradient-subtle">
      <div className="container mx-auto px-4">
        <div className="text-center mb-12">
          <div className="flex items-center justify-center mb-4">
            <Calculator className="h-8 w-8 text-primary mr-3" />
            <h2 className="text-3xl lg:text-4xl font-bold text-foreground">
              {t('quote.title')}
            </h2>
          </div>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
            {language === 'en' 
              ? 'Calculate your landscaping materials cost with instant delivery pricing'
              : 'Calcule el costo de sus materiales de jardinería con precios de entrega instantáneos'
            }
          </p>
        </div>

        <div className="max-w-4xl mx-auto grid lg:grid-cols-2 gap-8">
          {/* Quote Builder */}
          <Card className="p-6 shadow-card">
            <h3 className="text-xl font-semibold mb-6">
              {language === 'en' ? 'Build Your Quote' : 'Construya Su Cotización'}
            </h3>
            
            <div className="space-y-4">
              {/* Product Selection */}
              <div>
                <label className="block text-sm font-medium mb-2">{t('quote.product')}</label>
                <Select
                  value={currentItem.product_id}
                  onValueChange={(value) => setCurrentItem(prev => ({ ...prev, product_id: value }))}
                >
                  <SelectTrigger aria-label={language === 'en' ? 'Select a product' : 'Seleccionar un producto'}>
                    <SelectValue placeholder={language === 'en' ? 'Select a product' : 'Seleccionar un producto'} />
                  </SelectTrigger>
                  <SelectContent>
                    {products.map((product) => (
                      <SelectItem key={product.id} value={product.id}>
                        {getProductName(product)} - ${getProductPrice(product)} {getUnitLabel(product)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Quantity and Unit */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label htmlFor="quote-quantity" className="block text-sm font-medium mb-2">{t('quote.quantity')}</label>
                  <Input
                    id="quote-quantity"
                    type="number"
                    min="1"
                    value={currentItem.quantity}
                    onChange={(e) => setCurrentItem(prev => ({ ...prev, quantity: parseInt(e.target.value) || 1 }))}
                    aria-label={language === 'en' ? 'Quantity' : 'Cantidad'}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">{t('quote.unit')}</label>
                  <Select
                    value={currentItem.unit}
                    onValueChange={(value) => setCurrentItem(prev => ({ ...prev, unit: value }))}
                  >
                    <SelectTrigger aria-label={language === 'en' ? 'Select unit' : 'Seleccionar unidad'}>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="pallet">{language === 'en' ? 'Pallet' : 'Palet'}</SelectItem>
                      <SelectItem value="half-pallet">{language === 'en' ? 'Half Pallet' : 'Medio Palet'}</SelectItem>
                      <SelectItem value="piece">{language === 'en' ? 'Piece' : 'Pieza'}</SelectItem>
                      <SelectItem value="yard">{language === 'en' ? 'Cubic Yard' : 'Yarda Cúbica'}</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Miles for Delivery */}
              <div>
                <label className="block text-sm font-medium mb-2">{t('quote.miles')}</label>
                <Input
                  type="number"
                  min="0"
                  value={miles}
                  onChange={(e) => setMiles(parseInt(e.target.value) || 0)}
                  placeholder={language === 'en' ? 'Enter miles for delivery' : 'Ingrese millas para entrega'}
                />
                <p className="text-xs text-muted-foreground mt-1">
                  {language === 'en' 
                    ? 'Distance from our location. Delivery fee is calculated based on roundtrip miles.'
                    : 'Distancia desde nuestra ubicación. La tarifa de entrega se calcula basada en millas de ida y vuelta.'
                  }
                </p>
              </div>

              <Button onClick={addItem} className="w-full" disabled={!currentItem.product_id}>
                <Plus className="mr-2 h-4 w-4" />
                {t('quote.add')}
              </Button>
            </div>
          </Card>

          {/* Quote Summary */}
          <Card className="p-6 shadow-card">
            <h3 className="text-xl font-semibold mb-6">
              {language === 'en' ? 'Quote Summary' : 'Resumen de Cotización'}
            </h3>
            
            {items.length === 0 ? (
              <p className="text-muted-foreground text-center py-8">
                {language === 'en' ? 'Add items to see your quote' : 'Agregue artículos para ver su cotización'}
              </p>
            ) : (
              <div className="space-y-4">
                {/* Items List */}
                <div className="space-y-3">
                  {items.map((item) => (
                    <div key={item.id} className="flex justify-between items-center p-3 bg-muted rounded-lg">
                      <div>
                        <p className="font-medium">{item.product_name}</p>
                        <p className="text-sm text-muted-foreground">
                          {item.quantity} {item.unit} @ ${item.unit_price}
                        </p>
                      </div>
                      <div className="text-right flex items-center gap-2">
                        <p className="font-medium">${item.total_price.toFixed(2)}</p>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => removeItem(item.id)}
                          className="text-destructive p-2"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>

                {/* Totals */}
                <div className="border-t pt-4 space-y-2">
                  <div className="flex justify-between">
                    <span>{t('quote.subtotal')}</span>
                    <span>${subtotal.toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span>{t('quote.delivery')} ({miles} {language === 'en' ? 'miles' : 'millas'})</span>
                    <span>${deliveryFee.toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between text-lg font-bold border-t pt-2">
                    <span>{t('quote.total')}</span>
                    <span>${total.toFixed(2)}</span>
                  </div>
                </div>

                {/* Membership Disclaimer */}
                <div className="mb-4">
                  <MembershipDisclaimerForm showInline />
                </div>

                {/* Action Buttons */}
                <div className="space-y-2 pt-4">
                  {user ? (
                    <Button variant="hero" className="w-full" onClick={saveQuote}>
                      <Download className="mr-2 h-4 w-4" />
                      {language === 'en' ? 'Save Quote' : 'Guardar Cotización'}
                    </Button>
                  ) : (
                    <Button 
                      variant="hero" 
                      className="w-full" 
                      onClick={() => navigate(`/${language}/auth`, { 
                        state: { from: { pathname: `/${language}/quote` } } 
                      })}
                    >
                      <LogIn className="mr-2 h-4 w-4" />
                      {language === 'en' ? 'Sign In to Save Quote' : 'Iniciar Sesión para Guardar'}
                    </Button>
                  )}
                  <Button variant="outline" className="w-full">
                    <Mail className="mr-2 h-4 w-4" />
                    {t('quote.email')}
                  </Button>
                </div>
              </div>
            )}
          </Card>
        </div>
      </div>
    </section>
  );
};

export default EnhancedQuoteCalculator;